import {Component, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BaseComponent} from '../../core/base.component';
import {HeaderService} from '../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {AuthService} from '../../core/security/auth.service';
import {ReportsService} from '../reports.service';
import {ReportsService as ReportsApiService} from '../../api/services/reports.service';
import {ActivityTotalModel} from '../../api/models/activity-total-model';
import {GrandTotalModel} from '../../api/models/grand-total-model';
import {ActivityDashboardService} from './activity-dashboard.service';
import {appColors} from '../../core/styles/colors';
import {DateTime, Settings} from 'luxon';
import {DateUtils} from '../../core/utilities/date-time-helpers';
import {SelectedFilterValuesModel} from '../../dialogs/grid-filter/selected-filter-values.model';
import {FilterOptionModel} from '../../dialogs/grid-filter/filter-option.model';
import {DateRangeService} from '../../core/services/date-range.service';
import {MatDialog} from '@angular/material/dialog';
import {debounceTime} from 'rxjs/operators';
import { FlexModule } from '@angular/flex-layout';
import { NgIf, NgFor } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { GridFilterComponent } from '../../dialogs/grid-filter/grid-filter.component';
import { FilterRowAdditionalContentComponent } from '../../dialogs/grid-filter/filter-row-additional-content.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { DashboardCardComponent } from '../shared/dashboard-card.component';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { ActivityChartComponent } from './charts/activity-chart.component';
import { ActivityPieChartComponent } from './charts/activity-pie-chart.component';


@Component({
    selector: 'app-activity-dashboard',
    templateUrl: './activity-dashboard.component.html',
    styleUrls: ['./activity-dashboard.component.scss'],
    standalone: true,
    imports: [FlexModule, NgIf, MatProgressBar, GridFilterComponent, FilterRowAdditionalContentComponent, MatButton, MatIcon, DashboardCardComponent, MatCard, MatCardContent, ActivityChartComponent, ActivityPieChartComponent, MatCardTitle, NgFor]
})
export class ActivityDashboardComponent extends BaseComponent implements OnInit, OnDestroy {

  filterChangeEvent = new EventEmitter<any>();
  selectedFilterChange = new EventEmitter<SelectedFilterValuesModel[]>();

  selectedFilter: SelectedFilterValuesModel[] = new Array<SelectedFilterValuesModel>();
  filterOptions: FilterOptionModel[] = [];

  additionalFilter: any = {};

  colors = appColors;

  dashboardData: ActivityTotalModel = {} as ActivityTotalModel;
  grandTotals: GrandTotalModel = {} as GrandTotalModel;

  topByGroups: any = [];
  topByUsers: any = [];

  maxSeriesDate: Date;
  navFrom;
  navTo;

  timeSeriesType: 'line' | 'bar' = 'line';
  timeSeriesTotals: any = [];
  pieChartData: any = [];

  isLoading = true;

  constructor(public reportsService: ReportsService, public reportApiService: ReportsApiService,
              public activityDashboardService: ActivityDashboardService,
              public dateRangeService: DateRangeService,
              public dialog: MatDialog,
              route: ActivatedRoute, headerService: HeaderService, titleService: Title, authService: AuthService) {
    super(route, headerService, titleService, authService);
  }

  ngOnInit() {
    super.ngOnInit();
    console.log('my on init');

    this.initFilterOptions();

    this.filterChangeEvent
      .pipe(debounceTime(10))
      .subscribe(async (filter) => {
        this.additionalFilter = filter;
        this.isLoading = true;
        console.log(filter);
        const response = await this.reportApiService.activityDashboardGetActivityDashboardData({
          body: {
            domain: this.reportsService.selectedDomain || '',
            startDate: filter.startDate,
            endDate: filter.endDate,
          }
        }).toPromise();

        if (response) {
          this.activityDashboardService.dashboardData = response;
          this.dashboardData = this.activityDashboardService.dashboardData;

          this.mapData();
        }

        this.isLoading = false;
      });
  }

  mapData() {
    this.dashboardData = this.activityDashboardService.dashboardData;

    const serverGrandTotals = this.dashboardData.grandTotals[0] || {
      isIncoming: 0,
      isGroup: 0,
      isLocal: 0,
      isMailbox: 0,
      isMailboxExcludeInternals: 0,
      isMissed: 0,
      isMissedExcludeInternals: 0,
      isOutgoing: 0,
      answeredCalls: 0,
      avgTalkDuration: 0,
      avgTalkDurationExcludeInternals: 0,
      totalTalkDuration: 0
    };

    this.grandTotals = {
      isIncoming: serverGrandTotals.isIncoming,
      isGroup: serverGrandTotals.isGroup,
      isLocal: serverGrandTotals.isLocal,
      isMailbox: serverGrandTotals.isMailbox,
      isMailboxExcludeInternals: serverGrandTotals.isMailboxExcludeInternals,
      isMissedExcludeInternals: serverGrandTotals.isMissedExcludeInternals,
      isMissed: serverGrandTotals.isMissed,
      isOutgoing: serverGrandTotals.isOutgoing + serverGrandTotals.isLocal,
      answeredCalls: serverGrandTotals.answeredCalls,
      avgTalkDuration: serverGrandTotals.avgTalkDuration,
      avgTalkDurationExcludeInternals: serverGrandTotals.avgTalkDurationExcludeInternals,
      totalTalkDuration: serverGrandTotals.totalTalkDuration
    };

    this.topByGroups = this.dashboardData.topByGroup.map((item: any) => {
      return {
        number: item._id.key,
        totalCount: item.isIncoming,
        percentValue: item.isIncoming * 100 / this.grandTotals.isIncoming
      };
    });

    this.topByUsers = this.dashboardData.topByUser.map((item: any) => {
      return {
        number: item._id.key,
        totalCount: item.isIncoming,
        percentValue: item.isIncoming * 100 / this.grandTotals.isIncoming,
        outTotalCount: item.isOutgoing,
        outPercentValue: item.isOutgoing * 100 / this.grandTotals.isOutgoing,
        totalAllCount: item.isIncoming + item.isOutgoing,
        percentAllValue: item.isIncoming * 100 / (item.isIncoming + item.isOutgoing),
      };
    });

    this.pieChartData = [
      {
        calls: this.grandTotals.isIncoming,
        color: appColors.primaryGreen,
        extension: 'Incoming Calls',
        isVisible: true,
      }, {
        calls: this.grandTotals.isOutgoing,
        color: appColors.primaryBlue,
        extension: 'Outgoing Calls',
        isVisible: true,
      }, {
        calls: this.grandTotals.isMissed,
        color: appColors.primaryRed,
        extension: 'Missed Calls',
        isVisible: true,
      }, {
        calls: this.grandTotals.isMailbox,
        color: appColors.orange,
        extension: 'Mailbox Calls',
        isVisible: true,
      }
    ];

    let maxDate = DateTime.fromISO('1790-01-01');
    this.timeSeriesTotals = this.dashboardData.timeSeriesTotals.map((item: any) => {

      const key = item._id;
      const localDateTime =
        DateTime
          .utc(key.year, key.month, key.day, key.hour, key.minutes)
          .setZone(Settings.defaultZone);

      if (localDateTime > maxDate) {
        maxDate = localDateTime;
      }

      return {
        date: localDateTime.toJSDate(),
        dateStr: localDateTime.toMillis(),
        isIncoming: item.isIncoming,
        isOutgoing: item.isOutgoing,
        isMissed: item.isMissed,
        // isGroup: item.isGroup,
        // isLocal: item.isLocal,
        isMailbox: item.isMailbox,
      };
    });

    this.maxSeriesDate = DateUtils.localDateFromDateTime(maxDate);
    this.navTo = DateUtils.localDateFromDateTime(maxDate);
    this.navFrom = DateUtils.localDateFromDateTime(maxDate.plus({'hours': -this.activityDashboardService.chartSelectedItem.action}));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  onSelectedItemsChanged($event: SelectedFilterValuesModel[]) {
    this.selectedFilter = $event;
  }

  onFilterChanged(filter: any) {
    this.filterChangeEvent.emit(filter);
  }

  initFilterOptions(): void {
    this.filterOptions = [
      {
        filterKey: 'searchText',
        multiValue: false,
        showAlways: true,
        type: 'string',
        title: 'Any'
      },
    ];
  }

  onRefreshClicked() {
    this.filterChangeEvent.emit(this.additionalFilter);
  }
}
